import React from "react";
import { filterMap } from "@hex-insights/core";
import {
	BooleanField,
	BooleanFieldInputProps,
	DateTimeField,
	FieldIf,
	RadioField,
	TextField,
	TextFieldInputProps,
} from "@hex-insights/forms";
import { Icon } from "../../../Components/Icon";
import { SelectTextInput } from "../../../Components/SelectTextInput";
import {
	EnrollmentApplication as EnrollmentApplicationType,
	useEnrollmentApplicationGuardianAddressListLazyQuery,
} from "../../../Utilities";
import { EnrollmentApplicationGuardianFormat } from "../../Format";
import { EnrollmentApplicationGuardianFormValues } from "../../FormUtilities";
import { BooleanDetailedInput, NameOrderField } from "../_General";
import { CountrySelectField } from "../Country";
import { EnrollmentApplicationSelectField } from "../EnrollmentApplication";
import { GuardianSelectField } from "../Guardian";
import { BaseFieldProps } from "../shared";
import * as EnrollmentApplicationGuardianSelectField from "./Select";

/**
 * Generic props for fields of the EnrollmentApplicationGuardian model.
 */
type FieldProps<
	K extends keyof EnrollmentApplicationGuardianFormValues.Base = keyof EnrollmentApplicationGuardianFormValues.Base,
> = BaseFieldProps<Pick<EnrollmentApplicationGuardianFormValues.Base, K>>;

/**
 * Generic props for fields of the EnrollmentApplicationGuardian model that only appear in the detail form.
 */
type DetailFieldProps<
	K extends keyof EnrollmentApplicationGuardianFormValues.Detail = keyof EnrollmentApplicationGuardianFormValues.Detail,
> = BaseFieldProps<Pick<EnrollmentApplicationGuardianFormValues.Detail, K>>;

/**
 * Field component for the `name` field of the EnrollmentApplicationGuardian model.
 */
export function Name({ formState, ...props }: DetailFieldProps<"name">) {
	return <TextField formState={formState} name="name" {...props} />;
}

/**
 * Field component for the `givenName` field of the EnrollmentApplicationGuardian model.
 */
export function GivenName({ formState, ...props }: FieldProps<"givenName">) {
	return <TextField formState={formState} name="givenName" {...props} />;
}

/**
 * Field component for the `middleName` field of the EnrollmentApplicationGuardian model.
 */
export function MiddleName({ formState, ...props }: FieldProps<"middleName">) {
	return <TextField formState={formState} name="middleName" optional {...props} />;
}

/**
 * Field component for the `familyName` field of the EnrollmentApplicationGuardian model.
 */
export function FamilyName({ formState, ...props }: FieldProps<"familyName">) {
	return <TextField formState={formState} name="familyName" {...props} />;
}

/**
 * Field component for the `nameOrder` field of the EnrollmentApplicationGuardian model.
 */
export function NameOrder(props: FieldProps<"nameOrder" | "givenName" | "familyName">) {
	const { givenName, familyName } = props.formState.formValues;

	return <NameOrderField {...props} name="nameOrder" givenName={givenName} familyName={familyName} nickname="" />;
}

/**
 * Field component for the `dateOfBirth` field of the EnrollmentApplicationGuardian model.
 */
export function DateOfBirth({ formState, ...props }: FieldProps<"dateOfBirth">) {
	return <DateTimeField formState={formState} name="dateOfBirth" optional precision="day" {...props} />;
}

/**
 * Field component for the `gender` field of the EnrollmentApplicationGuardian model.
 */
export function Gender({ formState, ...props }: FieldProps<"gender">) {
	return <TextField formState={formState} name="gender" optional Input={GenderInput} {...props} />;
}

const genderOptions = [{ value: "Male" }, { value: "Female" }];

function GenderInput(props: TextFieldInputProps) {
	return (
		<SelectTextInput
			options={genderOptions}
			{...props}
			showInvalid={props.className?.includes("invalid") ?? false}
			className={undefined}
		/>
	);
}

/**
 * Field component for the `relationshipType` field of the EnrollmentApplicationGuardian model.
 */
export function RelationshipType({
	formState,
	label = "Relationship to Applicant(s)",
	...props
}: FieldProps<"relationshipType">) {
	return (
		<TextField formState={formState} name="relationshipType" label={label} Input={RelationshipTypeInput} {...props} />
	);
}

const relationshipTypeOptions = [{ value: "Mother" }, { value: "Father" }];

function RelationshipTypeInput(props: TextFieldInputProps) {
	return (
		<SelectTextInput
			options={relationshipTypeOptions}
			{...props}
			showInvalid={props.className?.includes("invalid") ?? false}
			className={undefined}
		/>
	);
}

/**
 * Field component for the `relationshipStatus` field of the EnrollmentApplicationGuardian model.
 */
export function RelationshipStatus({
	formState,
	label = "Marital Status",
	...props
}: FieldProps<"relationshipStatus">) {
	return (
		<RadioField
			formState={formState}
			name="relationshipStatus"
			label={label}
			options={EnrollmentApplicationGuardianFormValues.relationshipStatusOptions}
			blankValue={null}
			optional
			{...props}
		/>
	);
}

/**
 * Field component for the `emailAddress` field of the EnrollmentApplicationGuardian model.
 */
export function EmailAddress({ formState, ...props }: FieldProps<"emailAddress">) {
	return <TextField formState={formState} name="emailAddress" {...props} />;
}

/**
 * Field component for the `phoneNumber` field of the EnrollmentApplicationGuardian model.
 */
export function PhoneNumber({ formState, ...props }: FieldProps<"phoneNumber">) {
	return <TextField formState={formState} name="phoneNumber" {...props} />;
}

/**
 * Field component for the `addressLine1` field of the EnrollmentApplicationGuardian model.
 */
export function AddressLine1({ formState, ...props }: FieldProps<"addressLine1">) {
	return <TextField formState={formState} name="addressLine1" {...props} />;
}

/**
 * Field component for the `addressLine2` field of the EnrollmentApplicationGuardian model.
 */
export function AddressLine2({ formState, ...props }: FieldProps<"addressLine2">) {
	return <TextField formState={formState} name="addressLine2" optional {...props} />;
}

/**
 * Field component for the `addressCity` field of the EnrollmentApplicationGuardian model.
 */
export function AddressCity({ formState, label = "City", ...props }: FieldProps<"addressCity">) {
	return <TextField formState={formState} name="addressCity" label={label} {...props} />;
}

/**
 * Field component for the `addressPostalCode` field of the EnrollmentApplicationGuardian model.
 */
export function AddressPostalCode({ formState, label = "Postal Code", ...props }: FieldProps<"addressPostalCode">) {
	return <TextField formState={formState} name="addressPostalCode" label={label} optional {...props} />;
}

/**
 * Field component for the `addressStateOrProvince` field of the EnrollmentApplicationGuardian model.
 */
export function AddressStateOrProvince({
	formState,
	label = "State or Province",
	...props
}: FieldProps<"addressStateOrProvince">) {
	return <TextField formState={formState} name="addressStateOrProvince" label={label} optional {...props} />;
}

/**
 * Field component for the `isPrimary` field of the EnrollmentApplicationGuardian model.
 */
export function IsPrimary({ formState, ...props }: FieldProps<"isPrimary">) {
	return <BooleanField formState={formState} name="isPrimary" Input={IsPrimaryInput} {...props} />;
}

function IsPrimaryInput(props: BooleanFieldInputProps) {
	return (
		<BooleanDetailedInput
			{...props}
			IconComponent={Icon.Star}
			label="Primary Contact"
			description="This is a description of what that means so parents understand"
		/>
	);
}

/**
 * Field component for the `isFinancialContact` field of the EnrollmentApplicationGuardian model.
 */
export function IsFinancialContact({ formState, ...props }: FieldProps<"isFinancialContact">) {
	return <BooleanField formState={formState} name="isFinancialContact" Input={IsFinancialContactInput} {...props} />;
}

function IsFinancialContactInput(props: BooleanFieldInputProps) {
	return (
		<BooleanDetailedInput
			{...props}
			IconComponent={Icon.Coins}
			label="Financial Contact"
			description="This is a description of what that means so parents understand"
		/>
	);
}

/**
 * Field component for the `isEmergencyContact` field of the EnrollmentApplicationGuardian model.
 */
export function IsEmergencyContact({ formState, ...props }: FieldProps<"isEmergencyContact">) {
	return <BooleanField formState={formState} name="isEmergencyContact" Input={IsEmergencyContactInput} {...props} />;
}

function IsEmergencyContactInput(props: BooleanFieldInputProps) {
	return (
		<BooleanDetailedInput
			{...props}
			IconComponent={Icon.Warning}
			label="Emergency Contact"
			description="This is a description of what that means so parents understand"
		/>
	);
}

/**
 * Field component for the `isAuthorizedForPickup` field of the EnrollmentApplicationGuardian model.
 */
export function IsAuthorizedForPickup({ formState, ...props }: FieldProps<"isAuthorizedForPickup">) {
	return (
		<BooleanField formState={formState} name="isAuthorizedForPickup" Input={IsAuthorizedForPickupInput} {...props} />
	);
}

function IsAuthorizedForPickupInput(props: BooleanFieldInputProps) {
	return (
		<BooleanDetailedInput
			{...props}
			IconComponent={Icon.CarProfile}
			label="Authorized for Pick Up"
			description="This is a description of what that means so parents understand"
		/>
	);
}

/**
 * Field component for the `livesWithStudent` field of the EnrollmentApplicationGuardian model.
 */
export function LivesWithStudent({ formState, ...props }: FieldProps<"livesWithStudent">) {
	return <BooleanField formState={formState} name="livesWithStudent" Input={LivesWithStudentInput} {...props} />;
}

function LivesWithStudentInput(props: BooleanFieldInputProps) {
	return (
		<BooleanDetailedInput
			{...props}
			IconComponent={Icon.House}
			label="Lives With Student"
			description="This is a description of what that means so parents understand"
		/>
	);
}

/**
 * Field component for the `isPrimaryOnApplication` field of the EnrollmentApplicationGuardian model.
 */
export function IsPrimaryOnApplication({ formState, ...props }: FieldProps<"isPrimaryOnApplication">) {
	return <BooleanField formState={formState} name="isPrimaryOnApplication" {...props} />;
}

export namespace AddressCountry {
	export type SelectProps = FieldProps<"addressCountryID"> &
		Omit<
			CountrySelectField.SelectProps<EnrollmentApplicationGuardianFormValues.Base>,
			"formState" | "name" | "optional"
		>;

	/**
	 * Field component for the `addressCountry` edge of the EnrollmentApplicationGuardian model.
	 */
	export function Select({ formState, label = "Country", ...props }: SelectProps) {
		return <CountrySelectField.Select formState={formState} name="addressCountryID" label={label} {...props} />;
	}
}

export namespace EnrollmentApplication {
	export type SelectProps = FieldProps<"enrollmentApplicationID"> &
		Omit<
			EnrollmentApplicationSelectField.SelectProps<EnrollmentApplicationGuardianFormValues.Base>,
			"formState" | "name" | "optional"
		>;

	/**
	 * Field component for the `enrollmentApplication` edge of the EnrollmentApplicationGuardian model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <EnrollmentApplicationSelectField.Select formState={formState} name="enrollmentApplicationID" {...props} />;
	}
}

export namespace Guardian {
	export type SelectProps = FieldProps<"guardianID"> &
		Omit<
			GuardianSelectField.SelectProps<EnrollmentApplicationGuardianFormValues.Base>,
			"formState" | "name" | "optional"
		>;

	/**
	 * Field component for the `guardian` edge of the EnrollmentApplicationGuardian model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <GuardianSelectField.Select formState={formState} name="guardianID" optional {...props} />;
	}
}

export namespace TargetAddressGuardian {
	export type SelectProps = FieldProps<"targetAddressGuardianID"> &
		Omit<
			EnrollmentApplicationGuardianSelectField.SelectProps<EnrollmentApplicationGuardianFormValues.Base>,
			"formState" | "name" | "optional"
		> & {
			enrollmentApplicationID?: EnrollmentApplicationType["id"] | null;
		};

	/**
	 * Field component for the `targetAddressGuardian` edge of the EnrollmentApplicationGuardian model.
	 */
	export function Select({
		formState,
		label = "Address",
		enrollmentApplicationID,
		noOptional = true,
		...props
	}: SelectProps) {
		const [loadData, { loading, data }] = useEnrollmentApplicationGuardianAddressListLazyQuery({
			variables: {
				filters: { enrollmentApplicationIDEQ: enrollmentApplicationID },
			},
		});

		const {
			formEditing: { targetAddressGuardianID: isEditing },
		} = formState;
		React.useEffect(() => {
			console.log({ isEditing, enrollmentApplicationID });
			if (!isEditing || !enrollmentApplicationID) {
				return;
			}
			loadData();
		}, [isEditing, enrollmentApplicationID, loadData]);

		const options = React.useMemo(() => {
			if (!data) {
				return [];
			}
			return [
				...filterMap(
					data.enrollmentApplicationGuardianConnection.edges,
					(edge) => edge.node.targetAddressGuardian === null,
					(edge) => ({ value: edge.node.id, label: EnrollmentApplicationGuardianFormat.address(edge.node) }),
				),
				{ value: null, label: "New Address" },
			];
		}, [data]);

		return (
			<FieldIf formState={formState} name="targetAddressGuardianID" condition={!!enrollmentApplicationID}>
				<RadioField
					formState={formState}
					name="targetAddressGuardianID"
					label={label}
					isLoading={loading}
					options={options}
					optional
					noOptional={noOptional}
					{...props}
				/>
			</FieldIf>
		);
	}
}
